import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { StateEmployeeField } from "components/BalanceScorecard/DB-EmployeeDataMode";
import { useTranslation } from "translation/useTranslation";
import { resolveStateFillFields, buildStateDataToUpdate } from "./DB-fillFieldsHelpers";
import { fetchFields, getAllFields } from "store/fieldsSlice";
import { useDispatch, useSelector } from "react-redux";

export { resolveStateFillFields, buildStateDataToUpdate };

// No resolver, so partial fills are allowed. Editing a field auto-ticks its checkbox (compound
// sub-inputs tick their parent). Values and checked state are lifted to the parent.
function StateFieldForm({ checkboxFields, subToParent, checked, onCheck, onValues, initialValues, prepend = null }) {
  const dispatch = useDispatch();
  const { translate, editableTranslate } = useTranslation();
  const form = useForm({ defaultValues: initialValues || {} });
  const { watch } = form;

  const allFields = useSelector(getAllFields);

  // StateEmployeeField reads employee?.information?.[id] for init; blank for bulk fill.
  const stubEmployee = useMemo(() => ({ information: {} }), []);

  useEffect(() => {
    if (!allFields) dispatch(fetchFields());
  }, [allFields]);

  const fieldCustomNames = useMemo(() => {
    if (allFields) {
      return allFields.reduce((acc, item) => {
        acc[item.slug] = item.custom_name;
        return acc;
      }, {});
    }
    return null;
  }, [allFields]);

  useEffect(() => {
    const sub = watch((vals, { name }) => {
      onValues(vals);
      if (name) onCheck(subToParent[name] || name, true);
    });
    return () => sub.unsubscribe();
  }, [watch, onValues, onCheck, subToParent]);

  const fieldLabel = (field) => {
    return fieldCustomNames?.[field?.identifier] || editableTranslate(field?.translation_key, field?.name || field?.identifier);
  }

  return (
    <div className="row">
      {prepend}
      {checkboxFields.map((cf, i) => {
        const cbId = `state-fill-${cf.key}`;
        return (
          <div key={i} className="col-xl-4 col-lg-6 form-group">
            <div className="form-check mb-1">
              <input
                type="checkbox"
                id={cbId}
                data-testid={cbId}
                className="form-check-input"
                checked={!!checked[cf.key]}
                onChange={e => onCheck(cf.key, e.target.checked)}
              />
              <label className="form-check-label break-word ml-1" htmlFor={cbId}>{fieldLabel(cf.field)}</label>
            </div>
            <StateEmployeeField
              // Hide the field's own label (the checkbox above carries it), except checkbox_label
              // fields whose label row holds their value checkbox.
              field={cf.field?.checkbox_label ? cf.field : { ...cf.field, hide_label: true }}
              employee={stubEmployee}
              form={form}
              forEvaluator={false}
              booleanAsSelect={true}
            />
          </div>
        );
      })}
      {checkboxFields.length === 0 && !prepend && (
        <div className="col-12 text-muted" data-testid="empty-state">{translate('global.no_data', 'Keine Daten')}</div>
      )}
    </div>
  );
}

// Overwrite mode: one shared state form for all selected employees.
export function StateOverwriteForm({ sections, checked, setChecked, values, setValues, prepend = null }) {
  const { checkboxFields, subToParent } = useMemo(() => resolveStateFillFields(sections), [sections]);

  const onCheck = (key, isChecked) => setChecked(prev => {
    if (isChecked) return prev[key] ? prev : { ...prev, [key]: true };
    const next = { ...prev }; delete next[key]; return next;
  });

  return (
    <StateFieldForm
      checkboxFields={checkboxFields}
      subToParent={subToParent}
      checked={checked}
      onCheck={onCheck}
      onValues={setValues}
      initialValues={values}
      prepend={prepend}
    />
  );
}

// Per-instance mode: one state form per employee, with values/checked keyed by hash_id.
export function StateInstanceForm({ sections, hashId, checked, setChecked, values, setValues, prepend = null }) {
  const { checkboxFields, subToParent } = useMemo(() => resolveStateFillFields(sections), [sections]);

  const onCheck = (key, isChecked) => setChecked(prev => {
    const cur = { ...(prev[hashId] || {}) };
    if (isChecked) cur[key] = true; else delete cur[key];
    return { ...prev, [hashId]: cur };
  });

  const onValues = (vals) => setValues(prev => ({ ...prev, [hashId]: vals }));

  return (
    <StateFieldForm
      checkboxFields={checkboxFields}
      subToParent={subToParent}
      checked={checked[hashId] || {}}
      onCheck={onCheck}
      onValues={onValues}
      initialValues={values[hashId]}
      prepend={prepend}
    />
  );
}
